import React, { useState } from "react"

import { Flex, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"

import Icon from "../../Icon"
import Images from "../../Images"
import ButtonComponent from "../Button"
import ImageWithPreview from "../ImageWithPreview"
import Private from "../Private"
import VideoPlayerModal from "../VideoPlayerModal/VideoPlayerModal"
import { useTourClose } from "./index"

const env = process.env.REACT_APP_ENVIRONMENT

const { Text, Link } = Typography

const defaultOptions = {
  target: null,
  closeIcon: false,
  prevButtonProps: {
    size: "large",
    className: "ant-btn-lg btn"
  },
  nextButtonProps: {
    size: "large",
    className: "ant-btn-lg btn",
    children: (
      <Flex gap={8}>
        <span>Continue</span>
      </Flex>
    )
  }
}

const TutorialVideo = ({ url, isMobile = false }) => {
  const { t } = useTranslation()
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  return (
    <div>
      <VideoPlayerModal
        videoUrl={url}
        isOpened={isVideoModalOpen}
        close={() => setIsVideoModalOpen(false)}
      />
      <ButtonComponent
        height={!isMobile ? "36" : "100%"}
        width={isMobile && "100%"}
        icon={<Icon type={"watch_video_tutorial"} />}
        onClick={() => setIsVideoModalOpen(true)}
      >
        {t("Watch video tutorial")}
      </ButtonComponent>
    </div>
  )
}

const StepTitle = ({ title, currentStep, totalSteps }) => {
  const onClose = useTourClose()

  return (
    <Flex justify="space-between" align="start" gap={8} className="tour-title-row">
      <Flex align="center" gap={8} wrap="wrap" className="tour-title-content">
        <Text className="ant-tour-title">{title}</Text>
        {!!currentStep && !!totalSteps && (
          <span className="step-counter-badge">
            <span>{currentStep}</span>
            <span className="step-counter-slash">/</span>
            <span>{totalSteps} steps</span>
          </span>
        )}
      </Flex>
      {onClose && (
        <button
          type="button"
          className="tour-close-button"
          onClick={onClose}
          aria-label="Close tutorial"
        >
          <Icon type="cross_small" />
        </button>
      )}
    </Flex>
  )
}

const withStepCounters = (steps) =>
  steps.map((step, index) => {
    const titleProps = {
      title: step.title,
      currentStep: index + 1,
      totalSteps: steps.length
    }

    if (React.isValidElement(step.title) && step.title.type === StepTitle) {
      return {
        ...step,
        title: React.cloneElement(step.title, {
          currentStep: index + 1,
          totalSteps: steps.length
        })
      }
    }

    return {
      ...step,
      title: <StepTitle {...titleProps} />
    }
  })

const TourImagePreview = ({ src, alt }) => (
  <>
    <ImageWithPreview src={src} alt={alt} />
    <Text
      type="secondary"
      className="full-screen-text"
      style={{ alignItems: "center", gap: 2, fontSize: 12, marginTop: 8, display: "flex" }}
    >
      <Icon type="full_screen" /> Click the image to open it full screen
    </Text>
  </>
)

const coverWithVideo = (src, alt, videoUrl) => (
  <>
    <TourImagePreview src={src} alt={alt} />
    {videoUrl && (
      <div className="cover-tutorial-btn" style={{ width: "100%", marginTop: 12 }}>
        <TutorialVideo url={videoUrl} isMobile />
      </div>
    )}
  </>
)

export const stepsTourConnectPaymentProcessor = () => {
  const flows = {
    paypal: [
      {
        ...defaultOptions,
        title: "Integrate PayPal",
        cover: <img alt="paypal_step_1.png" src={Images["paypal_step_1"]} />,
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Sign in to your PayPal business account.</Text>
            <Text type="primary">In the header click Developer.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: "Integrate PayPal",
        cover: <img alt="paypal_step_2.png" src={Images["paypal_step_2"]} />,
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Click App & Credentials.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: "Integrate PayPal",
        cover: <img alt="paypal_step_3.png" src={Images["paypal_step_3"]} />,
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Click Default Application in the table.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: "Integrate PayPal",
        cover: <img alt="paypal_step_4.png" src={Images["paypal_step_4"]} />,
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Turn on Transaction search.</Text>
            <Text type="primary">Click Save Changes.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: "Integrate PayPal",
        cover: <img alt="paypal_step_5.png" src={Images["paypal_step_5"]} />,
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">
              Return to the previous page, the table contains 2 values needed:
            </Text>
            <Text type="primary">Client ID</Text>
            <Text type="primary">Secret</Text>
            <Text type="primary">You'll need these for the next step.</Text>
          </Space>
        )
      }
    ],
    chargebee: [
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Chargebee"} videoUrl={"https://youtu.be/AXqrcN-NsAg"} />,
        cover: coverWithVideo(
          Images["chargebee_step_1"],
          "chargebee_step_1.webp",
          "https://youtu.be/AXqrcN-NsAg"
        ),
        description: (
          <Flex vertical>
            <Text type="primary">Go to Settings, Configure Chargebee, API Keys</Text>
          </Flex>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Chargebee"} videoUrl={"https://youtu.be/AXqrcN-NsAg"} />,
        cover: coverWithVideo(
          Images["chargebee_step_2"],
          "chargebee_step_2.webp",
          "https://youtu.be/AXqrcN-NsAg"
        ),
        description: (
          <Space direction="vertical" size={8}>
            <Text type="primary">Click + Add API Key</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Chargebee"} videoUrl={"https://youtu.be/AXqrcN-NsAg"} />,
        cover: coverWithVideo(
          Images["chargebee_step_3"],
          "chargebee_step_3.webp",
          "https://youtu.be/AXqrcN-NsAg"
        ),
        description: (
          <Space direction="vertical" size={8}>
            <Text type="primary">Select Full-Access Key</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Chargebee"} videoUrl={"https://youtu.be/AXqrcN-NsAg"} />,
        cover: coverWithVideo(
          Images["chargebee_step_4"],
          "chargebee_step_4.webp",
          "https://youtu.be/AXqrcN-NsAg"
        ),
        description: (
          <Space direction="vertical" style={{ gap: 0 }}>
            <Text type="primary">
              Name the API Key:{" "}
              <Text underline type="primary">
                Chargeback.io
              </Text>
            </Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Chargebee"} videoUrl={"https://youtu.be/AXqrcN-NsAg"} />,
        cover: coverWithVideo(
          Images["chargebee_step_5"],
          "chargebee_step_5.webp",
          "https://youtu.be/AXqrcN-NsAg"
        ),
        description: (
          <Space direction="vertical" size={8}>
            <Space direction="vertical" size={0}>
              <Text type="primary">Copy your Site Key</Text>
            </Space>
          </Space>
        )
      }
    ],
    recurly: [
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Recurly"} videoUrl={"https://youtu.be/FrUlWuYMZkw"} />,
        cover: coverWithVideo(
          Images["recurly_step_1"],
          "recurly_step_1.webp",
          "https://youtu.be/FrUlWuYMZkw"
        ),
        description: (
          <Space direction="vertical" size={8}>
            <Space direction="vertical" size={0}>
              <Text type="primary">
                Go to Integrations, API Credentials, then Add Private API Key
              </Text>
            </Space>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Recurly"} videoUrl={"https://youtu.be/FrUlWuYMZkw"} />,
        cover: coverWithVideo(
          Images["recurly_step_2"],
          "recurly_step_2.webp",
          "https://youtu.be/FrUlWuYMZkw"
        ),
        description: (
          <Space direction="vertical" size={8}>
            <Space direction="vertical" size={0}>
              <Text type="primary">Name the API Key 'Chargeback.io', then click Save Changes</Text>
            </Space>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Recurly"} videoUrl={"https://youtu.be/FrUlWuYMZkw"} />,
        cover: coverWithVideo(
          Images["recurly_step_3"],
          "recurly_step_3.webp",
          "https://youtu.be/FrUlWuYMZkw"
        ),
        description: (
          <Space direction="vertical" size={8}>
            <Space direction="vertical" size={0}>
              <Text type="primary">Copy your Private API Key</Text>
            </Space>
          </Space>
        )
      }
    ],
    braintree: [
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Braintree"} videoUrl={"https://youtu.be/rYBlRoJgF28"} />,
        cover: coverWithVideo(
          Images["braintree_step_1"],
          "braintree_step_1.webp",
          "https://youtu.be/rYBlRoJgF28"
        ),
        description: (
          <Space direction="vertical" size={8}>
            <Space direction="vertical" size={0}>
              <Text type="primary">In the header, click the Settings icon, then select API</Text>
            </Space>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Braintree"} videoUrl={"https://youtu.be/rYBlRoJgF28"} />,
        cover: coverWithVideo(
          Images["braintree_step_2"],
          "braintree_step_2.webp",
          "https://youtu.be/rYBlRoJgF28"
        ),
        description: (
          <Space direction="vertical" size={8}>
            <Space direction="vertical" size={0}>
              <Text type="primary">On the API Keys page, click View under Private Key</Text>
            </Space>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Braintree"} videoUrl={"https://youtu.be/rYBlRoJgF28"} />,
        cover: coverWithVideo(
          Images["braintree_step_3"],
          "braintree_step_3.webp",
          "https://youtu.be/rYBlRoJgF28"
        ),
        description: (
          <Space direction="vertical" size={8}>
            <Space direction="vertical" size={0} style={{ maxWidth: "410px" }}>
              <Text type="primary">
                On the Client Library Key page, you'll need these 3 values for the next step:
              </Text>
              {["Public Key", "Private key", "Merchant ID"].map((item, i) => (
                <Text key={item} type="primary">
                  {i + 1}. {item}
                </Text>
              ))}
            </Space>
          </Space>
        )
      }
    ],
    slack: [
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Slack"} />,
        cover: <TourImagePreview src={Images["clack_step_1"]} alt="clack_step_1" />,
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">
              Enter a name for the Slack notification channel to be created
              <br /> in your workspace
            </Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Slack"} />,
        cover: <TourImagePreview src={Images["clack_step_2"]} alt="clack_step_2" />,
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">
              Select a workspace and click "Allow" to grant the permissions
              <br />
              needed to create a notification channel and send messages.
            </Text>
            <Text type="primary">Start getting notifications for all incoming alerts</Text>
          </Space>
        )
      }
    ],
    next: [
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Integrate Next Commerce"} videoUrl={"https://youtu.be/pi4ylUDpGAk"} />
        ),
        cover: coverWithVideo(
          Images["next29_step_1"],
          "next29_step_1",
          "https://youtu.be/pi4ylUDpGAk"
        ),
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: "410px" }}>
            <Text type="primary">
              To connect Next Commerce you will need Admin API Access Token and Store. When you are
              at your store page, click on dashboard.
            </Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Integrate Next Commerce"} videoUrl={"https://youtu.be/pi4ylUDpGAk"} />
        ),
        cover: coverWithVideo(
          Images["next29_step_2"],
          "next29_step_2",
          "https://youtu.be/pi4ylUDpGAk"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">At store's dashboard click at settings.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Integrate Next Commerce"} videoUrl={"https://youtu.be/pi4ylUDpGAk"} />
        ),
        cover: coverWithVideo(
          Images["next29_step_3"],
          "next29_step_3",
          "https://youtu.be/pi4ylUDpGAk"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Scroll down and choose API Access.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Integrate Next Commerce"} videoUrl={"https://youtu.be/pi4ylUDpGAk"} />
        ),
        cover: coverWithVideo(
          Images["next29_step_4"],
          "next29_step_4",
          "https://youtu.be/pi4ylUDpGAk"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Then create a new App.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Integrate Next Commerce"} videoUrl={"https://youtu.be/pi4ylUDpGAk"} />
        ),
        cover: coverWithVideo(
          Images["next29_step_5"],
          "next29_step_5",
          "https://youtu.be/pi4ylUDpGAk"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Give it a name Chargeback(not required) and save.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Integrate Next Commerce"} videoUrl={"https://youtu.be/pi4ylUDpGAk"} />
        ),
        cover: coverWithVideo(
          Images["next29_step_6"],
          "next29_step_6",
          "https://youtu.be/pi4ylUDpGAk"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Save store name (before .29next.store) and access token.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Integrate Next Commerce"} videoUrl={"https://youtu.be/pi4ylUDpGAk"} />
        ),
        cover: coverWithVideo(
          Images["next29_step_7"],
          "next29_step_7",
          "https://youtu.be/pi4ylUDpGAk"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">And go to Configure App Permissions.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Integrate Next Commerce"} videoUrl={"https://youtu.be/pi4ylUDpGAk"} />
        ),
        cover: coverWithVideo(
          Images["next29_step_8"],
          "next29_step_8",
          "https://youtu.be/pi4ylUDpGAk"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Then locate and select the checkbox for orders:read.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Integrate Next Commerce"} videoUrl={"https://youtu.be/pi4ylUDpGAk"} />
        ),
        cover: coverWithVideo(
          Images["next29_step_9"],
          "next29_step_9",
          "https://youtu.be/pi4ylUDpGAk"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">
              Finally, locate and check the boxes for transactions:read and <br />{" "}
              transactions:write permissions, then click save.
            </Text>
          </Space>
        )
      }
    ],
    "authorize.net": [
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Connect Authorize.net"} videoUrl={"https://youtu.be/Catf9uANMkE"} />
        ),
        cover: coverWithVideo(
          Images["authorize_step_1"],
          "authorize_step_1",
          "https://youtu.be/Catf9uANMkE"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Go to Account, then API Credentials & Keys</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Connect Authorize.net"} videoUrl={"https://youtu.be/Catf9uANMkE"} />
        ),
        cover: coverWithVideo(
          Images["authorize_step_2"],
          "authorize_step_2",
          "https://youtu.be/Catf9uANMkE"
        ),
        description: (
          <Space direction="vertical" size={0}>
            {[
              "Copy your API Login ID",
              "Generate a new Transaction Key",
              "Select “Disable Old Transaction Key Immediately”"
            ].map((item, i) => (
              <Text key={item} type="primary">
                {i + 1}. {item}
              </Text>
            ))}
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Connect Authorize.net"} videoUrl={"https://youtu.be/Catf9uANMkE"} />
        ),
        cover: coverWithVideo(
          Images["authorize_step_3"],
          "authorize_step_3",
          "https://youtu.be/Catf9uANMkE"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Verify your identity with a PIN sent to your email</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <StepTitle title={"Connect Authorize.net"} videoUrl={"https://youtu.be/Catf9uANMkE"} />
        ),
        cover: coverWithVideo(
          Images["authorize_step_4"],
          "authorize_step_4",
          "https://youtu.be/Catf9uANMkE"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Copy your Transaction Key</Text>
          </Space>
        )
      }
    ],
    nmi: [
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect NMI"} videoUrl={"https://youtu.be/XC6RVsSsY5A"} />,
        cover: coverWithVideo(Images["nmi_step_1"], "nmi_step_1", "https://youtu.be/XC6RVsSsY5A"),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">In the sidebar, go to Settings, then Security Keys</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect NMI"} videoUrl={"https://youtu.be/XC6RVsSsY5A"} />,
        cover: coverWithVideo(Images["nmi_step_2"], "nmi_step_2", "https://youtu.be/XC6RVsSsY5A"),
        description: (
          <Space direction="vertical" size={0}>
            {[
              <>
                Click <Text strong>Add a New Private Key</Text>
              </>,
              <>Enter a name for the key, such as “Chargeback.io”</>,
              <>Assign the key to the correct username</>,
              <>Select “Disable Old Transaction Key Immediately”</>
            ].map((item, i) => (
              <Text key={i} type="primary">
                {i + 1}. {item}
              </Text>
            ))}
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect NMI"} videoUrl={"https://youtu.be/XC6RVsSsY5A"} />,
        cover: coverWithVideo(Images["nmi_step_3"], "nmi_step_3", "https://youtu.be/XC6RVsSsY5A"),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Copy your API Key</Text>
          </Space>
        )
      }
    ],
    airwallex: [
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Airwallex"} videoUrl={"https://youtu.be/oSZlHosn7-o"} />,
        cover: coverWithVideo(
          Images["airwallex_step_1"],
          "airwallex_step_1",
          "https://youtu.be/oSZlHosn7-o"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">In the sidebar, go to Settings, then Developer</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Airwallex"} videoUrl={"https://youtu.be/oSZlHosn7-o"} />,
        cover: coverWithVideo(
          Images["airwallex_step_2"],
          "airwallex_step_2",
          "https://youtu.be/oSZlHosn7-o"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Go to API keys</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Airwallex"} videoUrl={"https://youtu.be/oSZlHosn7-o"} />,
        cover: coverWithVideo(
          Images["airwallex_step_3"],
          "airwallex_step_3",
          "https://youtu.be/oSZlHosn7-o"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Click New scoped API key</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Airwallex"} videoUrl={"https://youtu.be/oSZlHosn7-o"} />,
        cover: coverWithVideo(
          Images["airwallex_step_4"],
          "airwallex_step_4",
          "https://youtu.be/oSZlHosn7-o"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">For the name, enter Chargeback.io Integration</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Airwallex"} videoUrl={"https://youtu.be/oSZlHosn7-o"} />,
        cover: coverWithVideo(
          Images["airwallex_step_5"],
          "airwallex_step_5",
          "https://youtu.be/oSZlHosn7-o"
        ),
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: 410 }}>
            <Text type="primary">
              Under Payment Acceptance, only check Read and Write; then, click{" "}
              <Text strong>Create</Text>
            </Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Airwallex"} videoUrl={"https://youtu.be/oSZlHosn7-o"} />,
        cover: coverWithVideo(
          Images["airwallex_step_6"],
          "airwallex_step_6",
          "https://youtu.be/oSZlHosn7-o"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Click Create</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Airwallex"} videoUrl={"https://youtu.be/oSZlHosn7-o"} />,
        cover: coverWithVideo(
          Images["airwallex_step_7"],
          "airwallex_step_7",
          "https://youtu.be/oSZlHosn7-o"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Verify with required steps</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Airwallex"} videoUrl={"https://youtu.be/oSZlHosn7-o"} />,
        cover: coverWithVideo(
          Images["airwallex_step_8"],
          "airwallex_step_8",
          "https://youtu.be/oSZlHosn7-o"
        ),
        description: <Space direction="vertical" size={0} />
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Airwallex"} videoUrl={"https://youtu.be/oSZlHosn7-o"} />,
        cover: coverWithVideo(
          Images["airwallex_step_9"],
          "airwallex_step_9",
          "https://youtu.be/oSZlHosn7-o"
        ),
        description: (
          <Space direction="vertical" size={0}>
            <Text type="primary">Copy API Key (first value) and Client ID</Text>
          </Space>
        )
      }
    ],
    adyen: [
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Adyen"} />,
        cover: <TourImagePreview src={Images["adyen_step_1"]} alt="adyen_step_1" />,
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: 410 }}>
            <Text>
              In your Adyen dashboard, go to Developers {">"} API Credentials, then click Create new
              credential
            </Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Adyen"} />,
        cover: <TourImagePreview src={Images["adyen_step_1"]} alt="adyen_step_1" />,
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: 410 }}>
            <Text type="primary">
              Select Web service user, add a description (e.g., “Chargeback”), then click Create
              credential
            </Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Adyen"} />,
        cover: <TourImagePreview src={Images["adyen_step_3"]} alt="adyen_step_3" />,
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: 410 }}>
            <Text type="primary">
              Under Server settings, open the API key tab, copy the key, then click Save changes
            </Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Adyen"} />,
        cover: <TourImagePreview src={Images["adyen_step_4"]} alt="adyen_step_4" />,
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: 410 }}>
            <Text type="primary">
              To get your merchant name, click your Adyen account (upper-left), then click the copy
              icon
            </Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: <StepTitle title={"Connect Adyen"} />,
        cover: <TourImagePreview src={Images["adyen_step_5"]} alt="adyen_step_5" />,
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: 410 }}>
            <Text type="primary">
              To get your live prefix, go to Developers {">"} API URLs. On the API URLs page, find
              Prefix and click Copy
            </Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: (
          <Flex gap={16} vertical>
            <Icon type="info_warning_big" />
            Adyen limitation
          </Flex>
        ),
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: 410 }}>
            <Text type="secondary">
              Read before continuing. Chargeback can only automatically refund transactions created
              after you connect your Adyen integration. Transactions created before the integration
              is connected can't be automatically refunded. To refund those earlier transactions,
              you'll need to invite a Chargeback Support Manager to your Adyen account so we can
              help.
              <br />
              <br />
              If you'd like to invite a Support Manager, add{" "}
              <Link href="mailto:support@chargeback.io">support@chargeback.io</Link> as a partner.
              You can also do this later. If you skip it now, contact our support anytime and we'll
              help you set it up.
            </Text>
          </Space>
        )
      }
    ],
    maverick: [
      {
        ...defaultOptions,
        title: "Integrate Maverick Payments",
        cover: <TourImagePreview src={Images["maverick_step_1"]} alt="maverick_step_1" />,
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: 410 }}>
            <Text>
              Log in to Maverick and open the menu in the upper-right corner of the dashboard. From
              the dropdown menu, select Security.
            </Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: "Integrate Maverick Payments",
        cover: <TourImagePreview src={Images["maverick_step_2"]} alt="maverick_step_2" />,
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: 410 }}>
            <Text>
              Locate the Connected Apps section and click the + icon. Then, click Provide Connect
              ID, then enter {env === "prod" ? "4Kn9z29brz" : "nrd92omzQM"}.
            </Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: "Integrate Maverick Payments",
        cover: <TourImagePreview src={Images["maverick_step_3"]} alt="maverick_step_3" />,
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: 410 }}>
            <Text>Then Create restricted API key.</Text>
          </Space>
        )
      },
      {
        ...defaultOptions,
        title: "Integrate Maverick Payments",
        cover: <TourImagePreview src={Images["maverick_step_4"]} alt="maverick_step_4" />,
        description: (
          <Space direction="vertical" size={0} style={{ maxWidth: 410 }}>
            <Text>
              Navigate to the home page, locate the DBAs section, then copy only the number after
              the # symbol. Do not copy the text.
            </Text>
          </Space>
        )
      }
    ]
  }

  return Object.fromEntries(
    Object.entries(flows).map(([key, steps]) => [key, withStepCounters(steps)])
  )
}

export const getMaverickTooltipContent = (data) => {
  return (
    <div style={{ maxWidth: 350 }}>
      <Text strong style={{ color: "#7A7A83" }}>
        Connected terminals:
      </Text>

      <div style={{ marginTop: 4 }}>
        <Text style={{ color: "#fff" }}>
          <Private>{data?.selected_terminals?.map((t) => t.name).join(", ")}</Private>
        </Text>
      </div>

      <div style={{ marginTop: 4 }}>
        <Text style={{ color: "#7A7A83" }}>
          To update terminals, disconnect Maverick Payments and reconnect with the new terminal
          selection.
        </Text>
      </div>
    </div>
  )
}
